import {
  format,
  startOfMonth,
  startOfWeek,
  addDays,
  isSameMonth,
  isToday,
} from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "../../../../lib/utils";
import { EventFragment } from "../../../../__generated__/graphql";
import MonthlyCalendarSkeleton from "./components/MonhtlyCalendarSkeleton";
import { motion } from "motion/react";
import RemainingEvents from "./components/RemainingEvents";
import { useCalendar } from "../../context/hook";
import EventItem from "../EventItem";
import { DAYS_OF_WEEK } from "../types";

export default function MonthlyCalendar() {
  const { currentDate, events, loading, error } = useCalendar();

  const firstDayOfMonth = startOfMonth(currentDate);
  const firstDayOfCalendar = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });

  if (loading) return <MonthlyCalendarSkeleton />;
  if (error) return <div>Error: {error.message}</div>;

  const eventItem = (event: EventFragment | null) => {
    if (!event) return null;

    return <EventItem key={event.id} event={event} />;
  };

  const eventsInCell = (events: EventFragment[]) => {
    if (events.length === 0) return null;

    const eventLimit = 8;
    if (events.length > eventLimit) {
      return (
        <div className="flex flex-col gap-1">
          {events.slice(0, eventLimit).map((event) => eventItem(event))}
          <RemainingEvents events={events.slice(eventLimit)} />
        </div>
      );
    }

    return events.map((event) => eventItem(event));
  };

  const daysCells = () => {
    const dates = [...Array(42)].map((_, index) => {
      const date = addDays(firstDayOfCalendar, index);

      return {
        date,
        isCurrentMonth: isSameMonth(date, currentDate),
        events: events?.filter((event) => {
          if (!event) return false;

          const eventDate = new Date(event.startDate);
          return format(eventDate, "yyyy-MM-dd") === format(date, "yyyy-MM-dd");
        }),
      };
    });

    return (
      <div className="h-full w-full flex flex-col">
        <div className="flex text-secondary">
          {DAYS_OF_WEEK.map((day) => (
            <div
              key={day}
              className="w-[14.28%] p-2 text-sm font-semibold text-center select-none"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="h-[calc(100%-40px)] flex flex-col rounded-lg border border-border overflow-hidden">
          {[...Array(6)].map((_, rowIndex) => (
            <div key={rowIndex} className="flex border-border">
              {[...Array(7)].map((_, colIndex) => {
                const dateInfo = dates[rowIndex * 7 + colIndex];

                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={cn(
                      "w-[14.28%] p-1 transition-all duration-300 min-h-[105px]",
                      dateInfo.isCurrentMonth
                        ? "bg-card/50 text-primary"
                        : "bg-background text-secondary",
                      rowIndex !== 5 && "border-b border-input",
                      colIndex !== 6 && "border-r border-input"
                    )}
                  >
                    <div
                      className={cn(
                        "text-sm",
                        isToday(dateInfo.date) && "text-accent font-extrabold"
                      )}
                    >
                      {format(dateInfo.date, "d", { locale: fr })}
                    </div>

                    {dateInfo.events?.length && dateInfo.events.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {eventsInCell(dateInfo.events)}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <motion.div
        className="flex flex-col gap-4 h-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex-1 min-h-0">{daysCells()}</div>
      </motion.div>
    </>
  );
}
